import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { PasswordStrengthPolicyService } from '@lib/core/service';
import { Uuid } from '@lib/core/type';
import {
  ChangePasswordControlComponent,
  ChangePasswordControlValue,
  PasswordStrengthConfig,
} from '@lib/ui';
import { ConfigurableDialog, DialogModule, DialogService } from '@proget-shared/dialog';
import { CapsLockTooltipModule } from '@proget-shared/form/caps-lock-tooltip';
import { FormControlErrorModule } from '@proget-shared/form/form-control-error';
import { PasswordModule } from '@proget-shared/form/password';
import { ResponseError, ResponseErrorForm, ResponseErrorTemplateMapper } from '@proget-shared/helper/response-error';
import { TranslateModule } from '@proget-shared/translate';
import { ToastService } from '@proget-shared/ui/toast';
import { finalize } from 'rxjs';

import { UserCardRepository } from '../../user-card.repository';

@Component({
  selector: 'lib-change-password',
  imports: [
    ReactiveFormsModule,
    DialogModule,
    CapsLockTooltipModule,
    FormControlErrorModule,
    PasswordModule,
    TranslateModule,
    ChangePasswordControlComponent,
  ],
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.scss',
})
export class ChangePasswordComponent implements ConfigurableDialog {
  protected readonly strengthConfig = computed<PasswordStrengthConfig>(() => this.rawPasswordStrengthConfig() ?? {});
  protected readonly requestPending = signal(false);
  protected readonly errors = signal<ResponseErrorForm>({});

  protected readonly oldPassword = new FormControl('', [Validators.required]);
  protected readonly passwordControl = new FormControl<ChangePasswordControlValue>(null);

  private readonly toastService = inject(ToastService);
  private readonly userCardRepository = inject(UserCardRepository);
  private readonly dialogService = inject(DialogService);
  private readonly passwordStrengthPolicyService = inject(PasswordStrengthPolicyService);

  private readonly changePasswordControl = viewChild(ChangePasswordControlComponent);
  private readonly rawPasswordStrengthConfig = toSignal(this.passwordStrengthPolicyService.passwordStrengthConfig$);
  private readonly userId = signal<Uuid | null>(null);

  onConfiguration({ userId, items }: { userId: Uuid; items: any[] }): void {
    if (userId) {
      this.userId.set(userId);
    } else if (items?.length) {
      this.userId.set(items[0]?.uuid);
    }
  }

  protected close(): void {
    this.dialogService.reject(this);
  }

  protected onSubmit(): void {
    if (!this.userId()) {
      return;
    }

    if (this.passwordControl.invalid) {
      this.changePasswordControl()?.markAsTouched();

      return;
    }

    if (this.oldPassword.invalid) {
      this.oldPassword.markAsTouched();

      return;
    }

    const { newPassword } = this.passwordControl.value;

    this.errors.set({});
    this.requestPending.set(true);

    this.userCardRepository
      .changePassword(this.userId(), this.oldPassword.value, newPassword)
      .pipe(finalize(() => this.requestPending.set(false)))
      .subscribe({
        next: () => {
          this.toastService.success(
            'library.user_card.dialog.change_password.notification.password_change_success.title'
          );
          this.dialogService.resolve(this);
        },
        error: (error: ResponseError) => {
          this.errors.set(error.form);
          this.toastService.error(
            'library.user_card.dialog.change_password.notification.password_change_error.title',
            ResponseErrorTemplateMapper.map(error.messages)
          );
        },
      });
  }
}
